import { Flex, IconButton, Menu, MenuButton, MenuList, Text, useToken } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useContext } from "react";
import { useState } from "react";
import { MdOutlineEdit, MdOutlineEditOff } from "react-icons/md";
import { FormattedMessage } from "react-intl";

import { RanaButton, ThreeDotsIcon } from "@nens/ranalib";
import { MapIcon } from "@nens/ranalib";

import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { getPrettyTimeFromNow } from "@/utils/get-pretty-time";

import { PublicationVersionFunctionsContext } from "../../components/store-wrapper";
import {
  getIsEditable,
  getIsPublishable,
  getLastSaveTime,
  getLatestVersion,
  getPublishedVersion,
  getVersion,
} from "../../store/selectors";
import {
  getFiles,
  getIsDraftVersion,
  getIsPublic,
  getIsPublishedVersion,
  getIsSaveable,
} from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { CompleteFile } from "../../types/file-reference-with-file";
import { PublicationDetailEdit } from "../detail/components/edit";
import { usePublicationShowMap } from "../detail/hooks/use-show-map";
import { PublicationFilesAsUrlsModal } from "../file-as-url";
import { PublicationDetailFileShare, PublicationDetailShareCreate } from "../share";

import { PublicationDelete } from "./components/delete";
import { PublishPublication } from "./components/publish";
import { SaveChangesButton } from "./components/save-changes-button";
import { SwitchToVersionButton } from "./components/switch-to-version-button";

function ActionWrapper({ children, in: inProp }: { children: React.ReactNode; in: boolean }) {
  const isOpen = inProp;
  const [hidden, setHidden] = useState(!isOpen);
  const [mr] = useToken("sizes", [4]);

  return (
    <motion.div
      hidden={!isOpen && hidden}
      initial={false}
      onAnimationStart={() => setHidden(false)}
      onAnimationComplete={() => setHidden(!isOpen)}
      animate={{
        scaleX: isOpen ? 1 : 0,
        width: isOpen ? "auto" : 0,
        opacity: isOpen ? 1 : 0,
        marginRight: isOpen ? mr : 0,
      }}
      style={{ overflow: "hidden", whiteSpace: "nowrap", transformOrigin: "center" }}
    >
      {children}
    </motion.div>
  );
}

export function PublicationActionBar({ hideOpenViewer }: { hideOpenViewer?: boolean }) {
  const { closePublication, resetPublicationVersion } = useContext(
    PublicationVersionFunctionsContext
  );
  const files = usePublicationStore(getFiles);
  const isSaveable = usePublicationStore(getIsSaveable);
  const isPublic = usePublicationStore(getIsPublic);
  const isDraft = usePublicationStore(getIsDraftVersion);
  const isPublished = usePublicationStore(getIsPublishedVersion);
  const { canWriteProject } = useProjectActionAllowed();
  const lastSaveTime = usePublicationStore(getLastSaveTime);
  const version = usePublicationStore(getVersion);
  const [isMapOpen, setIsMapOpen] = usePublicationShowMap();
  const isPublishable = usePublicationStore(getIsPublishable);
  const isEditing = usePublicationStore(getIsEditable);
  const toggleEditing = usePublicationStore((state) => state.toggleEditing);

  // All publication files should be valid, defensive programming
  const hasValidFiles = (files as CompleteFile[]).some(
    (file) => file.file.descriptor.status?.id === "completed"
  );

  // Conditions variables for button visibility
  const showEditControls = !isPublic && canWriteProject;
  const enableMapButton = files.length > 0 && hasValidFiles && !isMapOpen;
  const canSaveChanges = showEditControls && isSaveable;
  const canPublish = showEditControls && isDraft;
  const showSaveOrPublishButtons = isEditing || canSaveChanges || isPublishable;
  const canSharePublication = showEditControls && isPublished;

  // Decide which version we are switching to
  const latestVersion = usePublicationStore(getLatestVersion);
  const publishedVersion = usePublicationStore(getPublishedVersion);

  const canSwitchToPublished = showEditControls && isDraft && !isPublished && !!publishedVersion;
  const canSwitchToDraft = showEditControls && isPublished && !isDraft && !!latestVersion;
  const showEditToggleButton =
    showEditControls && ((isPublished && isDraft) || (isEditing && !isDraft));
  let versionNumberToSwitchTo: number | null = null;

  if (canSwitchToDraft) versionNumberToSwitchTo = latestVersion;
  else if (canSwitchToPublished) versionNumberToSwitchTo = publishedVersion;

  return (
    <Flex alignItems="center">
      <ActionWrapper in={!isPublished}>
        <Text fontSize="sm" color="brand.sky" fontStyle="italic">
          {version === 0 && (
            <FormattedMessage
              id="publication_actions.no_versions_saved"
              defaultMessage="No versions saved yet"
            />
          )}
          {version > 0 && isDraft && (
            <FormattedMessage
              id="publication_actions.saved_date_from_now"
              defaultMessage="Last saved: {time}"
              values={{ time: getPrettyTimeFromNow(new Date(lastSaveTime)) }}
            />
          )}
          {version > 0 && !isDraft && (
            <FormattedMessage
              id="publication_actions.version_saved_date_from_now"
              defaultMessage="This version saved: {time}"
              values={{ time: getPrettyTimeFromNow(new Date(lastSaveTime)) }}
            />
          )}
        </Text>
      </ActionWrapper>
      {!hideOpenViewer && (
        <ActionWrapper in>
          <RanaButton
            isDisabled={!enableMapButton}
            variant={isDraft ? "secondary" : "primary"}
            onClick={() => setIsMapOpen(true)}
            leftIcon={<MapIcon />}
          >
            <FormattedMessage id="publication.open_viewer" defaultMessage="Open viewer" />
          </RanaButton>
        </ActionWrapper>
      )}
      <ActionWrapper in={showSaveOrPublishButtons && isDraft}>
        <SaveChangesButton canSaveChanges={canSaveChanges} />
      </ActionWrapper>
      <ActionWrapper in={showSaveOrPublishButtons && canPublish}>
        <PublishPublication />
      </ActionWrapper>
      <ActionWrapper in={!showSaveOrPublishButtons && canSharePublication}>
        <PublicationDetailShareCreate />
      </ActionWrapper>
      <ActionWrapper in={showEditToggleButton}>
        <RanaButton
          leftIcon={isEditing ? <MdOutlineEditOff /> : <MdOutlineEdit />}
          variant="tertiary"
          onClick={() => toggleEditing(resetPublicationVersion)}
        >
          {isSaveable && (
            <FormattedMessage id="publication.discard_changes" defaultMessage="Discard changes" />
          )}
          {!isSaveable && isEditing && (
            <FormattedMessage id="publication.stop_editing" defaultMessage="Stop editing" />
          )}
          {!isEditing && (
            <FormattedMessage id="publication.start_editing" defaultMessage="Edit publication" />
          )}
        </RanaButton>
      </ActionWrapper>
      <ActionWrapper in={canSwitchToPublished || canSwitchToDraft}>
        {versionNumberToSwitchTo !== null ? (
          <SwitchToVersionButton
            toVersion={canSwitchToDraft ? "draft" : "published"}
            versionNumberToSwitchTo={versionNumberToSwitchTo}
          />
        ) : null}
      </ActionWrapper>

      {showEditControls && (
        <Menu>
          <MenuButton as={IconButton} icon={<ThreeDotsIcon boxSize={6} />} variant="tertiary" />
          <MenuList>
            <PublicationDetailEdit />
            <PublicationDelete closePublication={closePublication} />
            <PublicationDetailFileShare />
            <PublicationFilesAsUrlsModal />
          </MenuList>
        </Menu>
      )}
    </Flex>
  );
}
